<template>
    <!-- Loading Skeleton -->
    <div v-if="showSkeleton" class="relative opacity-25 pointer-events-none">
        <div class="relative z-10 max-h-[75vh] overflow-y-auto">
            <!-- Search Suggestions Skeleton -->
            <div class="border-b border-b-keyline-secondary p-6">
                <!-- <h5 class="mb-2 font-medium type-tertiary px-2 text-sm">{{ $t("Search results") }}</h5> -->
                <ul>
                    <li v-for="n in 3" :key="n" class="w-full group px-2 py-1.5">
                        <div class="loading-value h-5 w-32 rounded"></div>
                    </li>
                </ul>
            </div>

            <!-- Product Results Skeleton -->
            <div class="p-8">
                <!-- <h5 class="mb-2 font-medium type-tertiary text-sm">{{ $t("Products") }}</h5> -->
                <ul class="flex flex-col">
                    <li v-for="n in 3" :key="n">
                        <div class="flex w-full gap-5 py-3 rounded-sm">
                            <!-- Product Image Skeleton -->
                            <div class="bg-v2-gray-130 flex items-center rounded-md shrink-0 size-20 dark:bg-v2-gray-50">
                                <div class="loading-value h-12 w-12 mx-auto rounded"></div>
                            </div>
                            <!-- Product Details Skeleton -->
                            <div class="grow space-y-1">
                                <div class="loading-value h-4 w-24 rounded"></div>
                                <div class="loading-value h-5 w-40 rounded"></div>
                                <div class="loading-value h-4 w-56 rounded"></div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>

    <!-- Actual Results -->
    <div v-else-if="showResults" class="relative">
        <!-- Top Shadow -->
        <div
            :style="{ opacity: 1 - shadowOpacity }"
            v-if="hasResults && searchTerm !== '' && !isLoading"
            class="absolute top-0 w-full h-12 bg-gradient-to-t from-transparent pointer-events-none to-black/15 z-20 dark:to-black/20"
        ></div>

        <div @scroll="handleScroll" ref="scrollContainer" class="relative z-10 max-h-[65vh] overflow-y-auto">
            <!-- Search Suggestions -->
            <div class="border-keyline-primary border-b p-6">
                <!-- <h5 class="mb-2 font-medium type-tertiary px-2 text-sm">{{ $t("Search results") }}</h5> -->
                <ul class="text-base">
                    <template v-for="(suggestions, key) in searchSuggestions" :key="key">
                        <li v-for="(suggestion, suggestionKey) in suggestions" :key="suggestionKey" v-show="suggestion.label?.length > 0" class="w-full group">
                            <a
                                :href="route('web.catalog.products.index', suggestion.query)"
                                class="w-full hover:bg-gray-tier-5 decoration-1 inline-flex group px-2 py-1.5 underline-offset-4 active:bg-gray-tier-6 hover:underline"
                                @click="$emit('suggestion-clicked', searchTerm)"
                            >
                                {{ suggestion.label }}
                            </a>
                        </li>
                    </template>
                </ul>
            </div>

            <!-- Product Results -->
            <div class="p-6">
                <!-- <h5 class="mb-2 font-medium type-tertiary text-sm px-4">{{ $t("Products") }}</h5> -->
                <ul id="resultList" class="flex flex-col">
                    <li v-for="(result, index) in searchResults" :key="result.id || index">
                        <Link
                            :href="route('web.catalog.products.show', result.slug)"
                            class="w-full hover:bg-gray-tier-5 group flex gap-6 px-2 py-4 active:bg-gray-tier-6"
                            :data="{ query: searchTerm }"
                            @click="$emit('result-clicked', result?.id)"
                        >
                            <div 
                                class=" flex items-center rounded-md shrink-0 size-24 group-active:bg-transparent"
                                :class="{ 'bg-v2-gray-130 dark:bg-v2-gray-50': !_.get(result,'display_thumbnails.0',false) }"
                            >
                                <template v-if="_.get(result,'display_thumbnails.0',false)">
                                    <img
                                        v-if="result.display_thumbnails.length"
                                        class="max-h-20 mx-auto"
                                        :src="'https://assets.seclock.com/image/private/t_thumb/' + result.display_thumbnails[0]"
                                        :alt="result.display_sku"
                                        loading="lazy"
                                    />
                                </template>
                                <span v-else class="size-24 flex bg-gray-tier-5 rounded-lg">
                                    <BaseSvg name="icon-product-empty" class="block p-7 fill-gray-tier-7" aria-hidden="true"/>
                                </span>
                            </div>
                            <div class="grow">
                                <div class="text-sm font-medium type-tertiary">{{ result.filter_manufacturer }}</div>
                                <span class="text-lg font-bold type-primary group-hover:underline decoration-1">
                                    {{ result.display_sku }}
                                </span>
                                <div class="type-secondary text-sm" aria-hidden="true">{{ result.display_short_description }}</div>
                            </div>
                        </Link>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Bottom Shadow -->
        <div
            v-if="hasResults && searchTerm !== '' && !isLoading"
            :style="{opacity: shadowOpacity}"
            class="absolute bottom-[calc(0.125rem-2px)] w-full h-12 bg-gradient-to-b from-transparent pointer-events-none to-black/10 z-20 dark:to-black/20"
        ></div>
    </div>
</template>

<script setup>
import { ref, inject, computed, watch } from 'vue';
import { Link } from '@inertiajs/vue3';
import { BaseSvg } from "@/Components";
import _ from "lodash";

const route = inject("route");

const props = defineProps({
    searchTerm: {
        type: String,
        default: ''
    },
    searchResultsTerm: {
        type: String,
        default: ''
    },
    searchResults: {
        type: Array,
        default: () => []
    },
    searchSuggestions: {
        type: Object,
        default: () => ({})
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    showResults: {
        type: Boolean,
        default: false
    },
    showSkeleton: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['suggestion-clicked', 'result-clicked']);

const scrollContainer = ref(null);
const shadowOpacity = ref(1);

const hasResults = computed(() => {
    return props.searchResults.length > 0 || Object.keys(props.searchSuggestions).length > 0;
});

const handleScroll = () => {
    if (!scrollContainer.value) return;

    const maxScroll = scrollContainer.value.scrollHeight - scrollContainer.value.clientHeight;
    shadowOpacity.value = 1 - Math.min(1, scrollContainer.value.scrollTop / maxScroll);
};

// Reset scroll position when search term changes
watch(() => props.searchTerm, () => {
    if (scrollContainer.value) {
        scrollContainer.value.scrollTop = 0;
        shadowOpacity.value = 1; // Reset shadow opacity when scroll resets
    }
});

defineExpose({
    scrollToTop: () => {
        if (scrollContainer.value) {
            scrollContainer.value.scrollTop = 0;
            shadowOpacity.value = 1; // Reset shadow opacity when scroll resets
        }
    }
});

</script>
